import { Injectable, Signal, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Claim, ClaimType } from '../../models/Claim';
import { Observable, of } from 'rxjs';
import { take, switchMap, catchError } from 'rxjs/operators';
import { HttpClientWrapperService } from '../http/http-client-wrapper.service';
import { ClaimsService } from '../claim/claims.service';
import { Charity } from '../../models/Charity';
import { CharityUser, InviteUserRequest, RespondToInviteRequest, PromoteUserRequest } from '../../models/CharityUser';
import { CharityInvite } from '../../models/CharityInvite';
import { AuthService } from '../auth/auth.service';
import { ApiUrlService } from '../api/api-url.service';

@Injectable({
  providedIn: 'root',
})
export class CharityManagementService {
  private readonly authService: AuthService = inject(AuthService);
  private readonly apiUrlService = inject(ApiUrlService);
  private get charityManagementApiUrl(): string { return this.apiUrlService.buildUrl(`/api/v1/charity-management`); }

  private readonly _claims = signal<Claim[]>([]);
  readonly claims: Signal<Claim[]> = this._claims.asReadonly();
  readonly claims$ = toObservable(this._claims);

  private readonly _currentCharityUser = signal<CharityUser | null>(null);
  readonly currentCharityUser: Signal<CharityUser | null> = this._currentCharityUser.asReadonly();
  readonly charityUser$ = toObservable(this._currentCharityUser);

  constructor(
    private httpClient: HttpClientWrapperService,
    private claimsService: ClaimsService
  ) {}

  submitCharityClaim(claim: Claim, files: File[], profileImage?: File | null): Observable<Claim> {
    claim.claimType = ClaimType.CHARITY;
    return this.claimsService.submitClaim(claim, files, profileImage);
  }

  loadCharityClaimsByUserId(userId: string): void {
    this.getCharityClaimsByUserId(userId).subscribe({
      next: (claims: Claim[]) => this._claims.set(claims)
    });
  }

  getCharityClaimsByUserId(userId: string): Observable<Claim[]> {
    return this.claimsService.getClaimsByUserId(userId, ClaimType.CHARITY);
  }

  getCharityClaim(id: string): Observable<Claim> {
    return this.claimsService.getClaim(id);
  }

  getPermittedCharities(): Observable<Charity[]> {
    return this.httpClient.get<Charity[]>(this.apiUrlService.buildUrl(`/api/v1/permitted-charities`));
  }

  inviteUser(request: InviteUserRequest): Observable<{ message: string }> {
    return this.httpClient.post<{ message: string }, InviteUserRequest>(`${this.charityManagementApiUrl}/invite-user`, request);
  }

  respondToInvite(request: RespondToInviteRequest): Observable<CharityUser> {
    return this.httpClient.postWithoutAuth<CharityUser, RespondToInviteRequest>(`${this.charityManagementApiUrl}/respond-to-invite`, request);
  }

  getCharityUsers(charityId: string): Observable<CharityUser[]> {
    return this.httpClient.get<CharityUser[]>(`${this.charityManagementApiUrl}/${charityId}/users`);
  }

  getCharityInvitations(charityId: string): Observable<CharityInvite[]> {
    return this.httpClient.get<CharityInvite[]>(`${this.charityManagementApiUrl}/${charityId}/invitations`);
  }

  removeUserAccess(charityId: string, userId: string): Observable<{ message: string }> {
    return this.httpClient.delete<{ message: string }>(`${this.charityManagementApiUrl}/remove-access?charityId=${charityId}&userId=${userId}`);
  }

  cancelInvitation(charityId: string, invitationId: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.charityManagementApiUrl}/cancel-invitation?charityId=${charityId}&invitationId=${invitationId}`);
  }

  resendInvitation(charityId: string, email: string): Observable<void> {
    return this.httpClient.post<void, null>(`${this.charityManagementApiUrl}/resend-invitation?charityId=${charityId}&email=${encodeURIComponent(email)}`, null);
  }

  promoteUser(request: PromoteUserRequest): Observable<void> {
    return this.httpClient.post<void, PromoteUserRequest>(`${this.charityManagementApiUrl}/promote-user`, request);
  }

  getCurrentCharityUser(charityId: string): Observable<CharityUser> {
    return this.httpClient.get<CharityUser>(`${this.charityManagementApiUrl}/${charityId}/users/me`);
  }

  setCurrentCharityUser(charityUser: CharityUser | null): void {
    this._currentCharityUser.set(charityUser);
  }

  loadCurrentCharityUser(charityId: string): void {
    this.authService.user$.pipe(
      take(1),
      switchMap((currentUser) => {
        if (!currentUser?.id) {
          return of(null);
        }
        return this.getCurrentCharityUser(charityId);
      }),
      catchError(() => of(null))
    ).subscribe({
      next: (charityUser) => {
        this._currentCharityUser.set(charityUser);
      }
    });
  }

  clearCurrentCharityUser(): void {
    this._currentCharityUser.set(null);
  }
}
